import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { trackShipment } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import "./Orders.css";

const formatINR = (amount) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export default function Orders({ embedded }) {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  
  // Real-time tracking stores
  const [trackingData, setTrackingData] = useState({});
  const [trackingLoading, setTrackingLoading] = useState({});

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      const { data } = await supabase
        .from("orders")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (data) setOrders(data);
      setLoading(false);
    })();
  }, [user]);

  const handleRowClick = (oid) => {
    setOpenId(openId === oid ? null : oid);
  };

  const handleFetchTracking = async (e, shiprocketOrderId, supabaseOrderId) => {
    e.stopPropagation();
    setTrackingLoading(prev => ({ ...prev, [supabaseOrderId]: true }));
    try {
      const res = await trackShipment(shiprocketOrderId);
      // Shiprocket data format: tracking -> tracking_data -> track_status, shipment_track, etc.
      setTrackingData(prev => ({ ...prev, [supabaseOrderId]: res.tracking || { error: "No tracking available yet. Waiting for courier assignment." } }));
    } catch (err) {
      setTrackingData(prev => ({ ...prev, [supabaseOrderId]: { error: "Tracking fetch failed. The AWB may not be generated yet." } }));
    } finally {
      setTrackingLoading(prev => ({ ...prev, [supabaseOrderId]: false }));
    }
  };

  if (!user)
    return (
      <div className={`mo ${embedded ? "mo--embed" : ""}`}>
        <div className="mo__empty">
          <span className="mo__empty-icon">👤</span>
          <p className="mo__empty-title">Please log in to view orders</p>
          <Link to="/login" className="mo__btn">Sign In</Link>
        </div>
      </div>
    );

  if (loading)
    return (
      <div className={`mo ${embedded ? "mo--embed" : ""}`}>
        <div className="mo__empty">
          <span className="mo__empty-icon mo__spin">↻</span>
          <p className="mo__empty-title">Loading orders…</p>
        </div>
      </div>
    );

  return (
    <div className={`mo ${embedded ? "mo--embed" : ""}`}>
      <div className="mo-container">
        <header className="mo__head">
          <h2 className="mo__title">My Orders</h2>
          <span className="mo__count">
            {orders.length
              ? `${orders.length} order${orders.length > 1 ? "s" : ""}`
              : "No orders yet"}
          </span>
        </header>

      {orders.length === 0 ? (
        <div className="mo__empty">
          <span className="mo__empty-icon">🛍</span>
          <p className="mo__empty-title">You haven't placed any orders yet</p>
          <Link to="/jewellery" className="mo__btn">Explore Collection</Link>
        </div>
      ) : (
        <div className="mo__list">
          {orders.map((o) => {
            const items = o.items || [];
            const addr = o.shipping_address || {};
            const open = openId === o.id;
            const shortId = (o.id || "").toString().slice(0, 8).toUpperCase();
            // Derive shipping from stored fee, or calculate from subtotal
            const storedShipping = o.shipping_fee;
            let shippingCharge;
            if (storedShipping !== undefined && storedShipping !== null && storedShipping > 0) {
              shippingCharge = storedShipping;
            } else {
              // Tiered: <199 = ₹49, 199-498 = ₹59, ≥499 = ₹79
              const sub = o.total_amount || 0;
              if (sub < 199) shippingCharge = 49;
              else if (sub < 499) shippingCharge = 59;
              else shippingCharge = 79;
            }
            const subtotal = o.total_amount || 0;
            const grandTotal = subtotal;
            const totalQty = items.reduce((s, it) => s + (it.qty || 1), 0);

            return (
              <article key={o.id} className={`mo-card ${open ? "mo-card--open" : ""}`}>

                {/* ── Status ribbon ── */}
                <div className="mo-card__ribbon">
                  <StatusPill status={o.status} />
                  <span className="mo-card__date">{formatDate(o.created_at)}</span>
                </div>

                {/* ── Product items ── */}
                <div className="mo-card__items">
                  {items.map((it, i) => {
                    const qty = it.qty || 1;
                    const lineTotal = (it.price || 0) * qty;
                    return (
                      <div className="mo-item" key={i}>
                        <img
                          className="mo-item__img"
                          src={it.img || ""}
                          alt={it.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect fill='%23f0ebe8' width='80' height='80' rx='8'/%3E%3Ctext fill='%23bbb' x='50%25' y='55%25' text-anchor='middle' font-size='11'%3ENo img%3C/text%3E%3C/svg%3E";
                          }}
                        />
                        <div className="mo-item__details">
                          <span className="mo-item__name">{it.name}</span>
                          <div className="mo-item__row2">
                            <span className="mo-item__qty">Qty: {qty}</span>
                            {qty > 1 && (
                              <span className="mo-item__unit-price">₹{formatINR(it.price)} each</span>
                            )}
                          </div>
                        </div>
                        <span className="mo-item__price">₹{formatINR(lineTotal)}</span>
                      </div>
                    );
                  })}
                </div>

                {/* ── Price breakdown ── */}
                <div className="mo-card__summary">
                  <div className="mo-summary__row">
                    <span>Subtotal ({totalQty} item{totalQty > 1 ? "s" : ""})</span>
                    <span>₹{formatINR(subtotal)}</span>
                  </div>
                  <div className="mo-summary__row">
                    <span>Shipping</span>
                    <span className="mo-summary__free">
                      ₹{formatINR(shippingCharge)}
                    </span>
                  </div>
                  <div className="mo-summary__row mo-summary__total">
                    <span>Order Total</span>
                    <span>₹{formatINR(grandTotal)}</span>
                  </div>
                </div>

                {/* ── Footer ── */}
                <div className="mo-card__footer">
                  <div className="mo-card__footer-left">
                    <span className="mo-card__oid">Order #{shortId}</span>
                    <button
                      className="mo-card__toggle"
                      onClick={() => handleRowClick(o.id)}
                    >
                      {open ? "Hide details ▲" : "View details ▼"}
                    </button>
                  </div>
                </div>

                {/* ── Expandable panel ── */}
                {open && (
                  <div className="mo-card__panel">
                    {(o.razorpay_payment_id || o.razorpay_order_id) && (
                      <DetailBlock title="Payment Information">
                        {o.razorpay_payment_id && <KV label="Payment ID" value={o.razorpay_payment_id} />}
                        {o.razorpay_order_id && <KV label="Razorpay Order" value={o.razorpay_order_id} />}
                        <KV label="Status" value={(o.status || "pending").toUpperCase()} highlight />
                      </DetailBlock>
                    )}
                    {addr.name && (
                      <DetailBlock title="Shipping Address">
                        <p className="mo-addr__name">
                          {addr.name} 
                          {addr.address_type && <span style={{ marginLeft: '8px', fontSize: '0.8em', padding: '2px 6px', background: '#f0ebe8', borderRadius: '4px', color: '#7a0f1b' }}>{addr.address_type}</span>}
                        </p>
                        
                        {/* New structured address format */}
                        {addr.house && addr.street ? (
                          <>
                            <p>{addr.house}, {addr.street}</p>
                            {addr.landmark && <p>Landmark: {addr.landmark}</p>}
                          </>
                        ) : (
                          /* Legacy single-string fallback */
                          <p>{addr.address}</p>
                        )}
                        
                        <p>{addr.city}, {addr.state} – {addr.pincode}</p>
                        {addr.phone && <p>Phone: {addr.phone}</p>}
                        {addr.email && <p>Email: {addr.email}</p>}
                      </DetailBlock>
                    )}
                    {(o.shiprocket_order_id || o.shipment_id || o.tracking_awb) && (
                      <DetailBlock title="Shipment Tracking">
                        {o.tracking_awb && (
                          <div style={{ padding: '8px 12px', background: '#f6fdf6', border: '1px solid #c8e6c9', borderRadius: '4px', marginBottom: '10px' }}>
                            <KV label="Tracking AWB" value={o.tracking_awb} highlight />
                          </div>
                        )}
                        {o.shiprocket_order_id && <KV label="Shiprocket Order Key" value={o.shiprocket_order_id} />}
                        
                        <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: '500', color: '#2b2b2b' }}>Live Courier Status</span>
                                <button 
                                    onClick={(e) => handleFetchTracking(e, o.shiprocket_order_id, o.id)}
                                    disabled={trackingLoading[o.id]}
                                    style={{ padding: '6px 12px', background: '#d4af37', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                                >
                                    {trackingLoading[o.id] ? "Fetching..." : "Live Track"}
                                </button>
                            </div>

                            {/* Live Tracking Engine Renderer */}
                            {trackingData[o.id] && (
                                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #dee2e6' }}>
                                    {trackingData[o.id].error ? (
                                        <div style={{ color: '#d32f2f', fontSize: '0.9rem' }}>{trackingData[o.id].error}</div>
                                    ) : (
                                        <>
                                           <KV label="AWB Number" value={trackingData[o.id]?.[Object.keys(trackingData[o.id] || {})[0]]?.tracking_data?.shipment_track?.[0]?.awb_code || "Processing..."} highlight />
                                           <KV label="Courier Partner" value={trackingData[o.id]?.[Object.keys(trackingData[o.id] || {})[0]]?.tracking_data?.shipment_track?.[0]?.courier_name || "Assigning..."} />
                                           <KV label="Current Activity" value={trackingData[o.id]?.[Object.keys(trackingData[o.id] || {})[0]]?.tracking_data?.track_status === 1 ? "Shipped / In Transit" : trackingData[o.id]?.[Object.keys(trackingData[o.id] || {})[0]]?.tracking_data?.shipment_track?.[0]?.current_status || "Pending Manifest"} />
                                           <KV label="Expected Delivery" value={trackingData[o.id]?.[Object.keys(trackingData[o.id] || {})[0]]?.tracking_data?.shipment_track?.[0]?.expected_date || "Calculating..."} />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                      </DetailBlock>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function StatusPill({ status }) {
  const s = (status || "pending").toLowerCase();
  const map = {
    processing: { bg: "rgba(212,175,55,0.18)", color: "#9a7800", text: "Processing" },
    shipped:    { bg: "rgba(21,101,192,0.14)", color: "#1565c0", text: "Shipped" },
    delivered:  { bg: "rgba(46,125,50,0.14)",  color: "#2e7d32", text: "Delivered" },
    cancelled:  { bg: "rgba(198,40,40,0.14)",  color: "#c62828", text: "Cancelled" },
    pending:    { bg: "rgba(122,15,27,0.12)",   color: "#7a0f1b", text: "Pending" },
  };
  const c = map[s] || map.pending;
  return <span className="mo-pill" style={{ background: c.bg, color: c.color }}>{c.text}</span>;
}

function DetailBlock({ title, children }) {
  return (
    <div className="mo-detail">
      <h5 className="mo-detail__title">{title}</h5>
      <div className="mo-detail__body">{children}</div>
    </div>
  );
}

function KV({ label, value, highlight }) {
  return (
    <div className="mo-kv">
      <span className="mo-kv__k">{label}</span>
      <span className={`mo-kv__v ${highlight ? "mo-kv__v--hl" : ""}`}>{value}</span>
    </div>
  );
}
